import type { VirtualItem } from '@tanstack/react-virtual'

import type { Card } from '~/db/types'

import { useExpandedStore } from '../../stores/expanded'

import { COLUMN_GAP_PX } from './const'
import type { ColumnCount } from './types'

const getTranslateY = (element: HTMLElement) => {
  const match = /translateY\((-?\d+(?:\.\d+)?)px\)/.exec(element.style.transform)
  if (!match?.[1]) return null

  return +match[1]
}

const setTranslateY = (element: HTMLElement, translateY: number) => {
  const previousTransform = element.style.transform
  if (!previousTransform.includes('translateY(')) {
    element.style.transform = `translateY(${translateY}px)`
    return
  }

  element.style.transform = previousTransform.replace(/translateY\([^)]*\)/, `translateY(${translateY}px)`)
}

const imperativeCardsAbsoluteShift = (
  cardsToShift: Card[],
  currentCardBoxBottom: number,
  columnCount: number,
  gap = 12,
) => {
  let previousCardBoxBottom = currentCardBoxBottom

  for (let rowStartIndex = 0; rowStartIndex < cardsToShift.length; rowStartIndex += columnCount) {
    const rowCards = cardsToShift.slice(rowStartIndex, rowStartIndex + columnCount)
    const currentCardTransformY = previousCardBoxBottom + gap
    let rowHeight = 0

    rowCards.forEach(card => {
      const cardElement = document.getElementById(card.id)
      const cardWrapper = cardElement?.closest('li')
      if (!cardElement || !cardWrapper) return console.error({ message: 'Card wrapper not found', cardId: card.id })

      setTranslateY(cardWrapper, currentCardTransformY)
      // Prefer the larger of content vs li — either can lag during expand/collapse.
      rowHeight = Math.max(rowHeight, cardElement.offsetHeight, cardWrapper.offsetHeight)
    })

    if (!rowHeight) return console.error({ message: 'Row height not found', rowStartIndex })
    previousCardBoxBottom = currentCardTransformY + rowHeight
  }
}

const getCardsToReposition = (visibleCards: Card[], allCards: Card[], cardId: string, columnCount: number) => {
  const currentCardIndex = visibleCards.findIndex(card => card.id === cardId)
  if (columnCount === 1) return visibleCards.slice(currentCardIndex + 1)

  const globalIndex = allCards.findIndex(card => card.id === cardId)
  const rowIndex = Math.floor(globalIndex / columnCount)
  const rowStart = (rowIndex + 1) * columnCount

  return visibleCards.filter(card => {
    const index = allCards.findIndex(entry => entry.id === card.id)
    return index >= rowStart
  })
}

/**
 * Opting out from the react rendering cycle to improve performance
 */
export const imperativeRepositionFactory =
  ({
    allCards,
    cardElement,
    cardId,
    columnCount,
    visibleCards,
  }: {
    allCards: Card[]
    cardElement: HTMLElement
    cardId: string
    columnCount: number
    visibleCards: Card[]
  }) =>
  (cardHeight: number) => {
    const cardLiWrapper = cardElement.closest('li')
    if (!cardLiWrapper) return console.error({ message: 'Card li wrapper not found', cardId })

    const transformY = getTranslateY(cardLiWrapper)
    if (transformY === null) return console.error({ message: 'Previous transform not found', cardId })

    const cardsBelowExpanded = getCardsToReposition(visibleCards, allCards, cardId, columnCount)
    if (!cardsBelowExpanded.length) return

    imperativeCardsAbsoluteShift(cardsBelowExpanded, transformY + cardHeight, columnCount)
  }

export const getTwoColumnStyles = (columnNumber: ColumnCount) => ({
  width: `calc(50% - ${COLUMN_GAP_PX / 2}px)`,
  translateXValue: columnNumber === 1 ? 'translateX(0px)' : `translateX(calc(100% + ${COLUMN_GAP_PX}px))`,
})

/** True when another card in the same virtualizer row is expanded. */
export const hasExpandedRowSibling = (cardId: string, allCards: { id: string }[], columnCount: number) => {
  if (columnCount === 1) return false

  const globalIndex = allCards.findIndex(card => card.id === cardId)
  if (globalIndex < 0) {
    console.error({ message: 'Card not found for hasExpandedRowSibling: ', cardId })

    return false
  }

  const rowStartIndex = Math.floor(globalIndex / columnCount)
  const { expandedSet } = useExpandedStore.getState()

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
    const sibling = allCards[rowStartIndex * columnCount + columnIndex]
    if (!sibling) return false
    if (sibling.id === cardId) continue
    if (expandedSet.has(sibling.id)) return true
  }

  return false
}

export const syncListItemPosition = ({
  cardId,
  columnCount,
  columnNumber,
  virtualRow,
}: {
  cardId: string
  columnCount: number
  columnNumber: ColumnCount
  virtualRow: VirtualItem
}) => {
  const cardListItemWrapper = document.getElementById(cardId)?.closest('li')
  if (!cardListItemWrapper) return console.error({ message: 'Card list item wrapper not found', cardId })

  cardListItemWrapper.style.height = `${virtualRow.size}px`
  cardListItemWrapper.style.transform = `translateY(${virtualRow.start}px)`

  if (columnCount === 2) {
    const styles = getTwoColumnStyles(columnNumber)
    cardListItemWrapper.style.width = styles.width
    if (columnNumber === 2)
      cardListItemWrapper.style.transform = `translateY(${virtualRow.start}px) ${styles.translateXValue}`
    return
  }

  cardListItemWrapper.style.left = '0px'
  cardListItemWrapper.style.width = '100%'
}
